import { Info } from 'lucide-react'
import { useId, useState } from 'react'
import type { TrustBreakdown } from '../../types/api'

function Row({ label, points, max, note }: { label: string; points: number; max: number; note: string }) {
  return (
    <li className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="font-semibold text-ink-900">{label}</div>
        <div className="text-[11px] text-ink-500">{note}</div>
      </div>
      <div className="shrink-0 font-bold text-ink-900">
        {points}
        <span className="font-normal text-ink-400">/{max}</span>
      </div>
    </li>
  )
}

function tenureNote(days: number): string {
  if (days >= 180) return 'On YellowDots 180+ days - full points'
  return `On YellowDots ${days} day${days === 1 ? '' : 's'} - grows to 20 at 180 days`
}

/**
 * The Trust tile on a dot's card, with a tooltip that shows how the number was reached.
 * Opens on hover, on tap (a phone has no hover) and on Enter or Space from the keyboard.
 */
export function TrustScoreTile({ score, breakdown }: { score?: number; breakdown?: TrustBreakdown }) {
  const [open, setOpen] = useState(false)
  const tipId = useId()

  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="w-full rounded-xl border border-ink-200 px-2 py-2"
        aria-describedby={breakdown ? tipId : undefined}
        aria-expanded={breakdown ? open : undefined}
        // Open, not toggle: a tap also fires mouseenter first, so a toggle would close it again.
        // Tapping anywhere else blurs the button and closes it.
        onClick={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false)
        }}
      >
        <div className="flex items-center justify-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-ink-400">
          Trust
          {breakdown && <Info size={11} aria-hidden="true" />}
        </div>
        <div className="text-lg font-extrabold text-ink-900">{score ?? '--'}</div>
        <div className="truncate text-[11px] text-ink-500">of 100</div>
      </button>

      {breakdown && open && (
        <div
          id={tipId}
          role="tooltip"
          className="absolute left-1/2 top-full z-20 mt-2 w-64 max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-xl border border-ink-200 bg-white p-3 text-left text-xs shadow-lg"
        >
          <div className="mb-2 font-semibold text-ink-900">How this score is worked out</div>
          <ul className="space-y-2">
            <Row
              label="Verification"
              points={breakdown.verificationPoints}
              max={40}
              note="Unverified 0 · V1 20 · V2 40"
            />
            <Row
              label="Ratings"
              points={breakdown.ratingPoints}
              max={40}
              note={
                breakdown.ratingCount === 0
                  ? 'No ratings yet - starts at 20'
                  : `Average of ${breakdown.ratingCount} rating${breakdown.ratingCount === 1 ? '' : 's'} (1★ = 0, 5★ = 40)`
              }
            />
            <Row label="Time on YellowDots" points={breakdown.tenurePoints} max={20} note={tenureNote(breakdown.tenureDays)} />
          </ul>
          <div className="mt-2 flex justify-between border-t border-ink-200 pt-2 font-bold text-ink-900">
            <span>Total</span>
            <span>{breakdown.total}/100</span>
          </div>
        </div>
      )}
    </div>
  )
}
